import { Box, Button, Collapse, Dialog, Divider, Typography } from "@mui/material";
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { ExamResponse } from "../../../../../../api/interfaces/preboard/Exam";
import { AttemptResponse } from "../../../../../../api/interfaces/preboard/Attempt";
import moment from "moment";
import { useState } from "react";
import PinnacleConfirmDeleteItem from "../../../../../../components/PinnacleConfirmDeleteItem/PinnacleConfirmDeleteItem";
import { handleApiError } from "../../../../../../utils/errorHandler";
import { pushNotification } from "../../../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { NotificationTypes } from "../../../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";
import { deleteExamAttempt } from "../../../../../../api/services/preboard/examinee";

type ModifyAttemptViewProps = {
    exam: ExamResponse;
    onAttemptDeleted?: () => void;
};

export const ModifyAttemptView: React.FC<ModifyAttemptViewProps> = ({ exam, onAttemptDeleted }) => {
    // Confirm delete dialog state
    const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState<boolean>(false);
    const [selectedAttemptInfo, setSelectedAttemptInfo] = useState<string>('');
    const [selectedAttemptId, setSelectedAttemptId] = useState<string>('');

    const [attempts, setAttempts] = useState<AttemptResponse[]>(exam.attempts);

    const [attemptToRemove, setAttemptToRemove] = useState<string | null>(null);


    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleConfirmDeleteAttempt = (attemptNumber: number, attemptInfo: AttemptResponse) => {
        setIsConfirmDeleteDialogOpen(true);
        setSelectedAttemptId(attemptInfo._id);
        setSelectedAttemptInfo(`Attempt #${attemptNumber} - ${moment(attemptInfo.dateTaken).format("MM-DD-YYYY - hh:mm A")}`)
    }

    const handleDeleteAttempt = async () => {
        setIsSubmitting(true);

        try {
            await deleteExamAttempt(selectedAttemptId);
            pushNotification({ details: "Attempt deleted successfully", type: NotificationTypes.Success });

            if (onAttemptDeleted) {
                onAttemptDeleted();
            }

            setIsConfirmDeleteDialogOpen(false);
            setAttemptToRemove(selectedAttemptId);
            setSelectedAttemptId("");
            setSelectedAttemptInfo("");
        } catch (error) {
            handleApiError(error, { notify: (msg) => pushNotification({ details: msg, type: NotificationTypes.Error }) });
        } finally {
            setIsSubmitting(false);
        }
    };



    return (
        <Box>
            <Typography
                variant="body2"
                sx={{ color: "#5840BA", fontWeight: 600, mb: 2 }}
            >
                {exam.name}
            </Typography>

            {attempts.map((attempt: AttemptResponse, index: number) => (
                <>
                    <Collapse
                        key={attempt._id}
                        in={attempt._id !== attemptToRemove}
                        timeout={300}
                        onExited={() => {
                            setAttempts((prev) => prev.filter((a) => a._id !== attempt._id));
                            setAttemptToRemove(null);
                        }}
                    >

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mt: 2,
                                mb: 2,
                            }}
                        >
                            <Box sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.2
                            }}>
                                <Typography variant="body2" sx={{ fontWeight: 400, color: "#373737", mb: 1 }}>
                                    Attempt #{index + 1}
                                </Typography>

                                <Typography variant="caption" sx={{ fontWeight: 400, color: "#818181" }}>
                                    Date Taken: {moment(attempt.dateTaken).format("MM-DD-YYYY - hh:mm A")}
                                </Typography>

                                <Typography variant="caption" sx={{ fontWeight: 400, color: "#818181" }}>
                                    Score / Items:{" "}
                                    {attempt.score === null ? "TBA" : attempt.score} /{" "}
                                    {exam.numberOfQuestions}
                                </Typography>
                            </Box>

                            <Button
                                size="small"
                                variant="outlined"
                                disabled={exam.numberOfAttempts === 0}
                                startIcon={<DeleteOutlineOutlinedIcon />}
                                onClick={() => handleConfirmDeleteAttempt((index + 1), attempt)}
                                sx={{
                                    fontSize: "12px",
                                    alignSelf: "flex-start",
                                    textTransform: "none",
                                    borderRadius: 1,
                                    minWidth: 80,
                                    height: "24px",
                                    border: "1px solid #D8D8DD",
                                    color: "#818181",
                                }}
                            >
                                Delete
                            </Button>
                        </Box>
                        <Divider />
                    </Collapse>
                </>
            ))}

            <Dialog
                open={isConfirmDeleteDialogOpen}
                onClose={() => setIsConfirmDeleteDialogOpen(!isConfirmDeleteDialogOpen)}
                aria-labelledby="responsive-dialog-title"
                sx={{
                    borderRadius: '0 !important'
                }}
            >
                <PinnacleConfirmDeleteItem
                    title="Delete Exam Attempt"
                    itemToDelete={selectedAttemptInfo}
                    onClose={() => setIsConfirmDeleteDialogOpen(false)}
                    onDelete={handleDeleteAttempt}
                    onCancel={() => setIsConfirmDeleteDialogOpen(false)}
                    isDeleting={isSubmitting}
                />
            </Dialog>
        </Box>
    );
};
